// @flow

import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import system from "system-components";

import theme from "../../../../themes/policystat/theme";
import { StyledFontAwesomeIcon } from "../../../partials/icons";

import { FLEX_HEADER_HEIGHT } from "pstat-anywhere/components/partials/HamburgerHeader";
import { TERTIARY_NAVBAR_HEIGHT } from "pstat-anywhere/components/partials/tertiary_nav/TertiaryNav";
import tocStyles from "pstat-anywhere/components/view/content/tocstyles";
import { withTenantContext } from "pstat-anywhere/context_providers/TenantContext";
import type { HeadingNode } from "pstat-anywhere/utils/domHelpers";
import RouterLink from "pstat-design-system/RouterLink";
import { STYLED_FLOATING_BUTTON_HEIGHT } from "pstat-design-system/shared/Button";
import { H3, Text } from "pstat-design-system/text";


const TocOl = styled("ol")`
  list-style: none;
`;

TocOl.displayName = "TocOl";

export const TocItem = ({
  item,
  onClick,
  activeItem,
  level
}: {
  item: HeadingNode
}) => {
  item.content = item.content.replace(/<img[^>]*>/gi, "");
  let active = null;
  if (activeItem) {
    active = activeItem.id === item.id;
  }
  const getOffset = level => {
    let total = 0;
    for (let i = 0; i <= level; i++) {
      total += theme.space[i];
    }
    total += theme.space[4];
    return total;
  };
  const offset = getOffset(level);
  return (
    <Fragment>
      <Text
        is="li"
        className={item.className}
        bg={active ? "secondary.0" : ""}
        id={`toc-${item.id}`}
        fontWeight={item.level % 2 === 0 ? "medium" : "normal"}
        size={item.level % 2 === 0 ? "small" : "normal"}
        pl={offset}
        mb={1}
      >
        <RouterLink
          to={{
            pathname: window.location.pathname,
            hash: `#${item.id}`
          }}
          color={active ? "white" : "secondary.0"}
          pl={1}
        >
          <span dangerouslySetInnerHTML={{ __html: item.content }} />
        </RouterLink>
      </Text>

      {item.children && (
        <TocLevel
          sublevels={item.children}
          activeItem={activeItem}
          level={level + 1}
        />
      )}
    </Fragment>
  );
};

const TocSection = ({ id, icon, label, count, activeItem }) => {
  const { t } = useTranslation();
  let active = null;
  if (activeItem) {
    active = activeItem.id === id;
  }

  return (
    <Fragment>
      <Text
        is="ul"
        bg={active ? "secondary.0" : ""}
        color="secondary.0"
        fontWeight="normal"
        size="normal"
        my={3}
        pl={4}
      >
        <StyledFontAwesomeIcon
          icon={icon}
          size="md"
          color={active ? "white" : "secondary.0"}
          mx="0"
        />
        <RouterLink
          to={{
            pathname: window.location.pathname,
            hash: "#" + id
          }}
          color={active ? "white" : "secondary.0"}
          pl={1}
        >
          {t(label, { count })}
        </RouterLink>
      </Text>
      <AttachmentsDivider />
    </Fragment>
  );
};

export const TocLevel = ({
  sublevels,
  activeItem,
  level
}: {
  sublevels: Array<HeadingNode>
}) => {
  if (sublevels.length > 0) {
    return (
      <TocOl>
        {sublevels.map(child => (
          <TocItem
            activeItem={activeItem}
            item={child}
            key={child.id}
            level={level}
          />
        ))}
      </TocOl>
    );
  }
  return null;
};

const AttachmentsDivider = system({
  mb: 2,
  mr: 5,
  borderBottom: 2,
  borderColor: "nav.90"
});

const TocContainer = system({
  bg: "nav.100",
  pb: 4,
  width: ["100vw", "100vw", "320px"],
  minHeight: ["100px", "100px", "400px"],
  boxShadow: `0px 2px 32px 0 rgba(0, 0, 0, 0.16), 0px 1px 6px 0 rgba(0, 0, 0, 0.06)`,
  borderRadius: `0 ${theme.radii[1]}px ${theme.radii[1]}px 0`,
  mb: 2
}).extend`
  max-height: ${props => props.maxHeight}px;
  overflow-y: auto;
  z-index: ${theme.layers.secondaryNav - 1};
  ${tocStyles};
  & ol {
    margin-top: ${theme.space[2]}px;
  & ol li {
    margin-bottom: ${theme.space[1]}px
  }
`;

TocContainer.displayName = "TocContainer";

const TocItemsContainer = system({
  is: "section",
  pl: 0
});

const TableOfContents = withTenantContext(
  ({
    tenant,
    rootNode,
    countAttachments,
    countStandards,
    countOneSource,
    activeItem
  }: {
    rootNode: HeadingNode
  }) => {
    const { t } = useTranslation();
    const [totalHeight, setTotalHeight] = useState(0);
    useEffect(() => {
      const primaryHeight = FLEX_HEADER_HEIGHT;
      const secondaryNav = document.getElementById("secondary-nav");
      let secondaryHeight = 0;
      if (secondaryNav != null) {
        secondaryHeight = secondaryNav.getBoundingClientRect().height;
      }

      const tertiaryHeight = TERTIARY_NAVBAR_HEIGHT;

      const TOCFloatingButtonHeight = STYLED_FLOATING_BUTTON_HEIGHT;
      const paddingHeight = 24 + 24 + 24; //button bot, table bot, table top

      setTotalHeight(
        window.innerHeight -
          (primaryHeight +
            secondaryHeight +
            tertiaryHeight +
            TOCFloatingButtonHeight +
            paddingHeight)
      );
    }, []);
    return (
      <TocContainer
        data-testId="toc-expanded-component"
        maxHeight={totalHeight}
      >
        <H3 bg="nav.10" size="lg" color="nav.100" height={50} px={2} py={3}>
          <StyledFontAwesomeIcon
            icon={["fas", "list-alt"]}
            color="nav.100"
            mr={2}
          />
          {t("documentControl.view.tableOfContents.tab.TOC.header")}
        </H3>
        <TocItemsContainer>
          <TocSection
            id={"attachments"}
            icon={["fal", "paperclip"]}
            label={"documentControl.view.tableOfContents.attachments.header"}
            count={countAttachments}
            activeItem={activeItem}
          />
          {countOneSource > 0 && (
            <TocSection
              id={"onesource-documents-panel"}
              icon={["fal", "paperclip"]}
              label={"documentControl.view.tableOfContents.onesource.header"}
              count={countOneSource}
              activeItem={activeItem}
            />
          )}
          {countStandards !== undefined && (
            <TocSection
              id={"standards"}
              icon={["far", "link"]}
              label={
                "documentControl.view.tableOfContents.vergeStandards.header"
              }
              count={countStandards}
              activeItem={activeItem}
            />
          )}
          <TocLevel
            sublevels={rootNode.children}
            activeItem={activeItem}
            level={0}
          />
        </TocItemsContainer>
      </TocContainer>
    );
  }
);

export default TableOfContents;
